import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Catálogos dinámicos: categorías y tipos de ítem
 *
 * Antes de esto, la CATEGORÍA de un producto era texto libre y las
 * opciones que veía el panel salían de `distinct("category")` sobre los
 * productos que YA existían. Con la base vacía no había ninguna opción y
 * no había forma de crear la primera: el desplegable solo sabía repetir
 * lo que alguien ya había escrito a mano en otra parte.
 *
 * Y el TIPO era un `z.enum` cerrado en el código: añadir "promoción"
 * exigía tocar el contrato, el modelo de Mongo, las etiquetas del panel
 * y desplegar las tres cosas.
 *
 * Este recurso es la respuesta a los dos: UNA colección con la lista de
 * opciones, administrable desde el panel, con la forma exacta de
 * `modules/product` y `modules/package`.
 *
 * ── Por qué UNA colección y no dos ──────────────────────────────────
 * Categoría y tipo son el mismo problema —una lista de opciones con
 * nombre en dos idiomas, sin duplicados, con unas cuantas intocables—
 * y sus reglas se escriben una sola vez. `kind` las separa, y el índice
 * único es `{ kind, normalized }`, así que "Detox" puede existir a la
 * vez como categoría y como tipo sin colisionar.
 *
 * ── El valor y la etiqueta son DOS cosas ────────────────────────────
 * `value` es lo que se guarda en el producto y lo que la lógica conoce
 * (`"promotion"`). `label` es lo que se lee, en los dos idiomas
 * ("Promoción" / "Promotion"). Separarlos es lo que permite corregir un
 * nombre mal escrito sin invalidar los productos que ya lo usan, y lo
 * que permite que la tienda en inglés no diga "Promoción".
 * ═══════════════════════════════════════════════════════════
 */
// ── De qué catálogo hablamos ─────────────────────────────
/**
 * Las cuatro listas que administra este recurso.
 *
 * ── Por qué las cuatro son la MISMA colección ───────────────────────
 * Categoría, tipo de ítem, BENEFICIO y COSTO ADICIONAL son el mismo
 * problema: una lista de opciones con nombre en dos idiomas, sin
 * duplicados, que crece desde el propio formulario del producto. Sus
 * reglas —normalizar para comparar, índice único, archivar en vez de
 * borrar, contar cuántos productos la usaban— se escriben una vez.
 *
 * Cuatro colecciones con cuatro servicios gemelos es exactamente lo que
 * este repositorio evita: el día que archivar necesite una confirmación,
 * tres de las cuatro se quedarían sin ella.
 */
export const CATALOG_KINDS = [
    "category",
    "product_type",
    "benefit",
    "extra_cost",
];
export const catalogKindSchema = z.enum(CATALOG_KINDS);
/**
 * Qué HACE un tipo de ítem, más allá de cómo se llame.
 *
 * ── Por qué existe este campo, y no basta con el `value` ────────────
 * El paso de clasificación cambia según el tipo: una PROMOCIÓN pide
 * descuento y fechas, un PAQUETE pide qué productos lo componen, un
 * PRODUCTO pide existencias. Eso es comportamiento atado a un valor
 * concreto — exactamente lo que un enum abierto rompe: en cuanto
 * cualquiera puede inventar un tipo, `type === "promotion"` deja de
 * cubrir los casos y el formulario no sabe qué enseñar.
 *
 * La salida no es cerrar el enum otra vez: es separar el NOMBRE del
 * COMPORTAMIENTO. La lista de nombres queda abierta —"Kit navideño",
 * "Bundle de temporada", lo que el negocio necesite— y cada nombre
 * declara a cuál de estos TRES comportamientos pertenece. La lógica
 * ramifica sobre el comportamiento, que sí es una lista cerrada y sí
 * está bajo control de quien programa.
 *
 * Así, añadir un tipo nuevo desde el panel no puede dejar al formulario
 * sin saber qué pintar: quien lo crea elige uno de los tres, y esa
 * elección es lo que el paso lee.
 */
export const PRODUCT_TYPE_BEHAVIORS = ["product", "package", "promotion"];
export const productTypeBehaviorSchema = z.enum(PRODUCT_TYPE_BEHAVIORS);
// ── El valor y su normalización ──────────────────────────
/**
 * `value`: lo que se guarda en el producto. Minúsculas, números, guiones
 * y guiones bajos. Es la MISMA forma que ya tenían los valores del enum
 * cerrado (`business_package`) y las categorías sembradas (`detox`), así
 * que todo lo que existe hoy sigue siendo válido sin tocar un documento.
 */
export const catalogValueSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, "Muy corto")
    .max(60, "Muy largo")
    .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, "Solo minúsculas, números, guiones y guiones bajos");
/**
 * La forma NORMALIZADA de un nombre, solo para comparar.
 *
 * ── El duplicado que este campo existe para impedir ─────────────────
 * "Detox", "detox" y "DETOX" son la misma categoría. "Oxigenación" y
 * "oxigenacion" también. Sin normalizar, la lista acaba con tres filas
 * que se ven casi iguales, el filtro de la tienda parte el catálogo en
 * tres montones y nadie entiende por qué su producto "no aparece en
 * Detox".
 *
 * Se quitan los acentos (NFD y fuera las marcas combinantes), se baja
 * todo a minúsculas y cualquier racha de lo que no sea letra o número
 * se vuelve UN espacio. Así "Detox  Elemental!" y "detox-elemental"
 * colisionan, que es lo correcto.
 *
 * Lo que se ESCRIBIÓ se conserva intacto en `label`: normalizar es para
 * comparar, nunca para mostrar. Quien escribe "Oxigenación" con acento
 * quiere ver "Oxigenación" con acento.
 */
export function normalizeCatalogName(raw) {
    return raw
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}
/**
 * El `value` que le toca a un nombre recién escrito.
 *
 * Se deriva del nombre en ESPAÑOL —que es el obligatorio— para que quien
 * crea una categoría no tenga que inventar además un identificador. El
 * resultado es la normalización con guiones en vez de espacios, o sea la
 * misma fórmula que `slugify` aplica a los productos.
 *
 * Devuelve `""` cuando el nombre no deja nada utilizable ("¿¡!?"): quien
 * llama decide qué hacer con eso, porque aquí no hay forma de dar un
 * error con sentido.
 */
export function catalogValueFromName(raw) {
    return normalizeCatalogName(raw).replace(/ /g, "-").slice(0, 60);
}
// ── La etiqueta, en los dos idiomas ──────────────────────
/**
 * Español obligatorio, inglés opcional. Misma regla que el contenido de
 * un producto y por la misma razón: el negocio opera en México y exigir
 * la traducción para poder guardar dejaría categorías sin crear
 * esperando a un traductor.
 */
/**
 * El techo son 200 caracteres, no 60.
 *
 * 60 bastaba mientras las dos listas eran nombres —"Detox", "Paquete"—.
 * Un BENEFICIO no es un nombre, es una frase entera ("Mejora la
 * elasticidad de la piel en ocho semanas"), y su techo en el producto
 * siempre fue 200: dejarlo en 60 aquí habría hecho que la mitad de los
 * beneficios que hoy se pueden escribir no cupieran en el catálogo que
 * viene a sustituir al texto libre. Los tres beneficios más largos del
 * catálogo sembrado miden 34 caracteres, así que nada existente cambia;
 * lo que cambia es lo que se podrá escribir mañana.
 *
 * Subir un máximo nunca invalida un dato guardado. Bajarlo sí, y por eso
 * la dirección de este cambio es la segura.
 */
export const catalogLabelSchema = z.object({
    es: z.string().trim().min(2, "Escribe el nombre").max(200, "Ese nombre es muy largo"),
    en: z.string().trim().min(2).max(200).optional(),
});
/** La etiqueta del idioma pedido, cayendo al español. */
export function catalogLabelFor(label, locale) {
    if (locale === "es")
        return label.es;
    return label.en?.trim() || label.es;
}
/**
 * La DESCRIPCIÓN de una opción, en los dos idiomas y toda opcional.
 *
 * ── Para qué existe, y por qué vive en el catálogo ──────────────────
 * Un costo adicional se captura con tres datos: nombre, descripción y
 * monto (Anahí, 26-ago). El monto es del PRODUCTO —200 pesos de envío
 * express no son 200 pesos en otro producto— pero el nombre y la
 * descripción son de la OPCIÓN: "Envío express" explica lo mismo en las
 * cuarenta fichas que lo ofrezcan.
 *
 * Ponerla en el producto habría significado escribir la misma
 * explicación una vez por ficha y en dos idiomas, y corregir una errata
 * cuarenta veces. Aquí se escribe una vez.
 *
 * Es opcional para TODAS las listas: una categoría no necesita
 * explicación y no se le va a pedir.
 */
export const catalogDescriptionSchema = z.object({
    es: z.string().trim().max(300, "Esa descripción es muy larga").optional(),
    en: z.string().trim().max(300, "Esa descripción es muy larga").optional(),
});
/** La descripción del idioma pedido, cayendo al español. `undefined` si no hay. */
export function catalogDescriptionFor(description, locale) {
    if (!description)
        return undefined;
    if (locale === "es")
        return description.es?.trim() || undefined;
    return description.en?.trim() || description.es?.trim() || undefined;
}
// ── Vivo o retirado ──────────────────────────────────────
/**
 * Dos estados, no tres.
 *
 * · `active`   — se ofrece al clasificar un producto.
 * · `archived` — ya no se ofrece, pero SIGUE SIENDO VÁLIDO en los
 *                productos que lo tienen puesto.
 *
 * No hay `draft`: una opción de catálogo o está disponible o no lo está.
 * Un borrador de categoría no significa nada.
 */
export const CATALOG_OPTION_STATUSES = ["active", "archived"];
export const catalogOptionStatusSchema = z.enum(CATALOG_OPTION_STATUSES);
// ── La opción ────────────────────────────────────────────
export const catalogOptionSchema = z.object({
    id: z.string(),
    kind: catalogKindSchema,
    /** Lo que se guarda en el producto. NO cambia nunca. */
    value: z.string(),
    label: catalogLabelSchema,
    /** Opcional y para cualquier lista; la piden sobre todo los costos. */
    description: catalogDescriptionSchema.optional(),
    /** La forma con la que se comparan los duplicados. La calcula el servidor. */
    normalized: z.string(),
    /** Solo para `product_type`: cuál de los tres comportamientos tiene. */
    behavior: productTypeBehaviorSchema.optional(),
    /**
     * Las que la LÓGICA conoce por nombre. No se borran y no se renombran
     * —su `value` y su `behavior` son inmutables— porque hay código que
     * ramifica sobre ellas. Su etiqueta SÍ se puede corregir: lo que se
     * lee no es lo que la lógica mira.
     */
    isSystem: z.boolean(),
    status: catalogOptionStatusSchema,
    sortOrder: z.number().int().default(0),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ── Los tipos de sistema ─────────────────────────────────
/**
 * Los tipos de ítem que el CÓDIGO conoce.
 *
 * ── Los tres primeros mandan en el formulario ───────────────────────
 * `product`, `package` y `promotion` son los tres que Anahí nombró y los
 * tres que cambian lo que se ve en el paso de clasificación.
 *
 * ── Los seis siguientes son el enum que había ───────────────────────
 * `supplement`, `membership`, `business_package`, `kit`, `service` y
 * `other` eran la lista cerrada anterior, y hay productos SEMBRADOS con
 * esos valores. Se conservan como opciones de sistema —con su
 * comportamiento declarado— para que abrir el enum no deje inválido ni
 * un solo documento de los que ya existen. Es la alternativa a una
 * migración que reescriba cuarenta y cinco fichas para renombrar lo
 * mismo.
 */
export const SYSTEM_PRODUCT_TYPES = [
    { value: "product", behavior: "product", label: { es: "Producto", en: "Product" }, sortOrder: 0 },
    { value: "package", behavior: "package", label: { es: "Paquete", en: "Package" }, sortOrder: 1 },
    {
        value: "promotion",
        behavior: "promotion",
        label: { es: "Promoción", en: "Promotion" },
        sortOrder: 2,
    },
    // ── El enum anterior, conservado ──────────────────────────────────
    {
        value: "supplement",
        behavior: "product",
        label: { es: "Suplemento", en: "Supplement" },
        sortOrder: 10,
    },
    {
        value: "membership",
        behavior: "product",
        label: { es: "Membresía", en: "Membership" },
        sortOrder: 11,
    },
    {
        value: "business_package",
        behavior: "package",
        label: { es: "Paquete de negocio", en: "Business package" },
        sortOrder: 12,
    },
    { value: "kit", behavior: "package", label: { es: "Kit", en: "Kit" }, sortOrder: 13 },
    {
        value: "service",
        behavior: "product",
        label: { es: "Servicio", en: "Service" },
        sortOrder: 14,
    },
    { value: "other", behavior: "product", label: { es: "Otro", en: "Other" }, sortOrder: 15 },
];
/** El comportamiento de un tipo de sistema, sin consultar la base. */
const SYSTEM_BEHAVIOR = Object.fromEntries(SYSTEM_PRODUCT_TYPES.map((entry) => [entry.value, entry.behavior]));
/**
 * Qué comportamiento tiene el tipo `value`, mirando primero las opciones
 * que se hayan cargado y cayendo a la tabla de sistema.
 *
 * El valor por omisión es `"product"` y no `undefined` a propósito: un
 * tipo que el formulario no reconoce tiene que enseñar ALGO, y lo más
 * inofensivo que puede enseñar es el paso de un producto normal. La
 * alternativa —un paso en blanco— deja a quien edita mirando una tarjeta
 * vacía sin saber si es un error o si de verdad no hay nada que llenar.
 */
export function behaviorOfType(value, options = []) {
    if (!value)
        return "product";
    const found = options.find((option) => option.kind === "product_type" && option.value === value);
    return found?.behavior ?? SYSTEM_BEHAVIOR[value] ?? "product";
}
// ── Crear y editar ───────────────────────────────────────
/**
 * Crear una opción.
 *
 * `value` es OPCIONAL: si no viene, el servidor lo deriva del nombre en
 * español. Es lo que hace posible que el formulario del producto ofrezca
 * "añadir categoría" con un solo campo de texto, sin pedirle a quien
 * administra que invente además un identificador técnico.
 */
export const catalogOptionInputSchema = z.object({
    kind: catalogKindSchema,
    label: catalogLabelSchema,
    description: catalogDescriptionSchema.optional(),
    value: catalogValueSchema.optional(),
    behavior: productTypeBehaviorSchema.optional(),
    sortOrder: z.number().int().default(0),
});
/**
 * Editar. Sin `value` ni `kind`: los dos son la identidad de la opción y
 * cambiarlos dejaría huérfano a cada producto que la tenga puesta —el
 * mismo daño que cambiarle el slug a un producto, y por el mismo motivo
 * no se puede.
 *
 * Sin valores por omisión: en una edición parcial, "no me lo dijiste"
 * significa "no lo toques". Es la trampa que `product.ts` documenta.
 */
export const catalogOptionUpdateSchema = z.object({
    label: catalogLabelSchema.optional(),
    description: catalogDescriptionSchema.optional(),
    behavior: productTypeBehaviorSchema.optional(),
    sortOrder: z.number().int().optional(),
    status: catalogOptionStatusSchema.optional(),
});
// ── Listado ──────────────────────────────────────────────
export const catalogListQuerySchema = z.object({
    kind: catalogKindSchema.optional(),
    status: catalogOptionStatusSchema.optional(),
});
export const catalogListSchema = z.object({
    items: z.array(catalogOptionSchema),
});
export const catalogListResponseSchema = responseSchema(catalogListSchema);
export const catalogOptionResponseSchema = responseSchema(z.object({ option: catalogOptionSchema }));
/**
 * Archivar devuelve CUÁNTOS productos seguían usando la opción.
 *
 * No es decorativo: es la única forma de que quien pulsa "eliminar" se
 * entere de que la opción no desapareció del catálogo, sigue viva en
 * treinta fichas y lo que hizo fue dejar de ofrecerla. Un `deleted: true`
 * a secas mentiría.
 */
export const catalogOptionArchivedResponseSchema = responseSchema(z.object({ archived: z.literal(true), productsUsing: z.number().int().min(0) }));
// ── Rutas ────────────────────────────────────────────────
export const CATALOG_ROUTES = {
    /** Solo super-admin: estas listas deciden cómo se clasifica el catálogo. */
    adminList: "/api/admin/catalog-options",
    adminCreate: "/api/admin/catalog-options",
    adminOne: (id) => `/api/admin/catalog-options/${id}`,
};
//# sourceMappingURL=catalog.js.map